// Render existing points (avatar dots) and the floating drama card.
//
// Card model:
//   - hover a dot → open card for that point (or transition in-place if open)
//   - click dot or click drama image → advance to the next point's card
//   - click expand icon → lightbox
//   - click outside / 关掉 / Esc → close

import { avatarUrl } from './avatars'
import { lightboxGenerate } from './api'
import { Ripple } from './ripple'
import type { Point, User } from './types'

const portal = () => document.getElementById('point-card-portal')!

// Shared state for the currently-open card.
let currentCardPoint: Point | null = null
let currentPointsList: Point[] = []
let currentAnchor: HTMLElement | null = null
let currentUser: User | null = null
let onPointsUpdate: ((p: Point) => void) | null = null
let onRegenAtPoint: ((p: Point) => void) | null = null

export function setCardContext(
  points: Point[],
  anchor: HTMLElement,
  user: User,
  onUpdate?: (p: Point) => void,
  onRegen?: (p: Point) => void,
) {
  currentPointsList = points
  currentAnchor = anchor
  currentUser = user
  onPointsUpdate = onUpdate || null
  onRegenAtPoint = onRegen || null
}

export function renderDots(
  layer: HTMLElement,
  points: Point[],
  onHover: (p: Point) => void,
  onAnchorClick: (p: Point) => void,
) {
  layer.innerHTML = ''
  for (const p of points) {
    if (p.status !== 'done') continue

    // 1) 白点锚点 — 精确点位，click 触发 drama 大图渐入
    const anchor = document.createElement('button')
    anchor.className = 'point-anchor'
    anchor.style.left = `${p.xRatio * 100}%`
    anchor.style.top = `${p.yRatio * 100}%`
    anchor.setAttribute('aria-label', `${p.userName} 在 ${p.targetLabel} 上点的`)
    anchor.addEventListener('mouseenter', () => onHover(p))
    anchor.addEventListener('click', (e) => {
      e.stopPropagation()
      onAnchorClick(p)
    })
    layer.appendChild(anchor)

    // 2) 头像 tag — 贴在锚点右上方，hover 出卡片
    const avatar = document.createElement('button')
    avatar.className = 'point-avatar'
    // 锚点中心在 (xRatio, yRatio)。把头像放到右上方约 14px 偏移。
    // 头像 20x20，锚点 12x12，间距感舒服的偏移：(+10, -22)。
    avatar.style.left = `calc(${p.xRatio * 100}% + 10px)`
    avatar.style.top = `calc(${p.yRatio * 100}% - 22px)`
    avatar.title = `${p.userName} · ${p.targetLabel}`
    const img = document.createElement('img')
    img.src = avatarUrl(p.userAvatarSeed, 48)
    img.alt = p.userName
    img.className = 'w-full h-full object-cover'
    avatar.appendChild(img)
    avatar.addEventListener('mouseenter', () => onHover(p))
    avatar.addEventListener('click', (e) => {
      // 触屏没有 hover —— 点头像也开卡片
      e.stopPropagation()
      onHover(p)
    })
    layer.appendChild(avatar)
  }
}

export function renderPulseDot(layer: HTMLElement, xRatio: number, yRatio: number): () => void {
  const el = document.createElement('div')
  el.className = 'dot-pulse'
  el.style.left = `${xRatio * 100}%`
  el.style.top = `${yRatio * 100}%`
  layer.appendChild(el)
  return () => el.remove()
}

/**
 * Show or update the drama card for `p`. If a card is already open for
 * a different point, the content transitions in place (image fades).
 */
export function showPointCard(p: Point, anchorEl?: HTMLElement) {
  if (anchorEl) currentAnchor = anchorEl
  const existingCard = document.querySelector<HTMLElement>('.point-card')
  if (existingCard && currentCardPoint) {
    transitionTo(p, existingCard)
  } else {
    closePointCard()
    renderFreshCard(p)
  }
  currentCardPoint = p
}

/**
 * If a card is already open for the same point → advance to the next point.
 * Otherwise, open/update the card to show `p`.
 */
export function advanceFromOrShow(p: Point) {
  if (currentCardPoint && currentCardPoint.id === p.id) {
    nextCard()
  } else {
    showPointCard(p)
  }
}

export function nextCard() {
  if (currentPointsList.length < 2 || !currentCardPoint) return
  const idx = currentPointsList.findIndex((x) => x.id === currentCardPoint!.id)
  if (idx < 0) return
  const next = currentPointsList[(idx + 1) % currentPointsList.length]
  showPointCard(next)
}

function runRegen(_card: HTMLElement) {
  if (!currentCardPoint) return
  const p = currentCardPoint
  closePointCard()
  // 把"涟漪 + 状态轮播 + API 调用 + 完成后开新卡"全交给 main.ts 那一套（与初始生成同款 UX）
  onRegenAtPoint?.(p)
}

function transitionTo(p: Point, card: HTMLElement) {
  const img = card.querySelector<HTMLImageElement>('.result-image')
  const sceneEl = card.querySelector<HTMLElement>('[data-field="scene"]')
  const directionEl = card.querySelector<HTMLElement>('[data-field="direction"]')
  const nameEl = card.querySelector<HTMLElement>('[data-field="name"]')
  const labelEl = card.querySelector<HTMLElement>('[data-field="label"]')
  const avatarEl = card.querySelector<HTMLImageElement>('[data-field="avatar"]')

  // 1) fade the image out
  if (img && p.dramaUrl) {
    img.classList.add('result-image-hidden')
    // pre-load the next image so the in-fade waits for pixels
    const next = new Image()
    const swap = () => {
      img.src = p.dramaUrl!
      // give the browser a tick to paint the new src before un-hiding
      requestAnimationFrame(() => requestAnimationFrame(() => img.classList.remove('result-image-hidden')))
    }
    next.onload = swap
    next.onerror = swap
    next.src = p.dramaUrl
  }

  // 2) snap text fields (kept simple per spec — only the image transitions)
  if (sceneEl) sceneEl.textContent = p.sceneZh
  if (directionEl) {
    const prefix = p.direction === 'before' ? '前情' : '后续'
    directionEl.textContent = `${prefix} · ${p.timeOffset}`
  }
  if (nameEl) nameEl.textContent = p.userName
  if (labelEl) labelEl.textContent = `点了「${p.targetLabel}」`
  if (avatarEl) avatarEl.src = avatarUrl(p.userAvatarSeed, 48)
}

function renderFreshCard(p: Point) {
  const root = portal()
  const isMobile = window.innerWidth < 640
  // 如果当前 lightbox 在开着，卡片要 z 高过 lightbox（z-60），并且去掉 dim backdrop
  // 否则用户看不到 lightbox 后面的图，体感不对
  const onLightbox = !!document.getElementById('flip-lightbox')

  const overlay = document.createElement('div')
  overlay.id = 'point-card-overlay'
  overlay.className = onLightbox
    ? 'fixed inset-0 z-[70] opacity-0 transition-opacity duration-200'
    : 'fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-[2px] opacity-0 transition-opacity duration-200'
  overlay.addEventListener('click', closePointCard)

  const card = document.createElement('div')
  const cardZ = onLightbox ? 'z-[71]' : 'z-50'
  card.className = isMobile
    ? `point-card fixed left-2 right-2 bottom-2 ${cardZ} max-h-[85vh] overflow-y-auto pb-safe transform translate-y-4 opacity-0 transition-all duration-300`
    : `point-card fixed ${cardZ} w-[380px] max-h-[80vh] overflow-y-auto opacity-0 scale-95 transition-all duration-200 origin-top-left`

  const directionLabel =
    p.direction === 'before'
      ? `前情 · ${escapeHtml(p.timeOffset)}`
      : `后续 · ${escapeHtml(p.timeOffset)}`

  card.innerHTML = `
    ${
      p.dramaUrl
        ? `<button data-image-click class="relative block w-full aspect-[4/3] bg-ink-100 overflow-hidden cursor-zoom-in group" aria-label="点击放大">
             <img src="${p.dramaUrl}" alt="" class="result-image w-full h-full object-cover" />
             <span data-zoom-icon class="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-ink-900/55 text-white flex items-center justify-center backdrop-blur-sm transition-all hover:bg-ink-900/80 hover:scale-110 shadow-pop" aria-label="放大查看">
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                 <path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/>
               </svg>
             </span>
           </button>`
        : ''
    }
    <div class="p-4 sm:p-5">
      <div class="flex items-center gap-2 mb-3">
        <img data-field="avatar" src="${avatarUrl(p.userAvatarSeed, 48)}" alt="" class="w-6 h-6 rounded-full" />
        <span data-field="name" class="text-[13px] text-ink-700">${escapeHtml(p.userName)}</span>
        <span data-field="label" class="text-[12px] text-ink-500">点了「${escapeHtml(p.targetLabel)}」</span>
      </div>
      <div class="mb-3">
        <span data-field="direction" class="inline-flex items-center gap-1 text-[11px] font-medium text-accent-deep bg-accent/10 px-2 py-0.5 rounded-full">${directionLabel}</span>
      </div>
      <p data-field="scene" class="text-[15px] leading-[1.55] text-ink-900 whitespace-pre-line">${escapeHtml(p.sceneZh)}</p>
      <div class="mt-4 flex items-center justify-between">
        <button data-regen class="flex items-center gap-1.5 text-[12px] text-ink-500 hover:text-accent transition-colors disabled:opacity-50" aria-label="重新生成这一段">
          <svg class="regen-icon w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2 L13.6 8.4 L20 10 L13.6 11.6 L12 18 L10.4 11.6 L4 10 L10.4 8.4 Z"/>
            <path d="M19 16 L19.6 18.4 L22 19 L19.6 19.6 L19 22 L18.4 19.6 L16 19 L18.4 18.4 Z" opacity="0.7"/>
          </svg>
          <span data-regen-label>再变一次</span>
        </button>
        <button data-close class="text-[13px] text-ink-500 hover:text-ink-900 transition-colors">关掉</button>
      </div>
    </div>
  `

  card.querySelector('[data-close]')!.addEventListener('click', closePointCard)
  if (p.dramaUrl) {
    // 整张图都是 zoom / push 触发区，角标只是视觉提示
    const btn = card.querySelector('[data-image-click]')!
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const cur = currentCardPoint
      if (!cur) return
      // 先关卡片，再开/推 lightbox — 不然新一层 lightbox 上还压着旧卡片
      closePointCard()
      showLightbox(cur)
    })
  }
  card.querySelector('[data-regen]')?.addEventListener('click', async (e) => {
    e.stopPropagation()
    await runRegen(card)
  })

  root.appendChild(overlay)
  root.appendChild(card)

  if (!isMobile && currentAnchor) {
    // 用实际尺寸（offsetWidth/Height 不算 scale 变换），避免被屏幕外
    const cardW = card.offsetWidth || 380
    const cardH = card.offsetHeight || 480
    const stage = currentAnchor.getBoundingClientRect()
    const cx = stage.left + p.xRatio * stage.width
    const cy = stage.top + p.yRatio * stage.height
    const margin = 12
    // 优先放在锚点右上方；空间不够就翻到左侧或上移
    let left = cx + 24
    let top = cy - 24
    if (left + cardW + margin > window.innerWidth) left = cx - cardW - 24
    if (left < margin) left = margin
    // 强约束：bottom 不能超 viewport
    if (top + cardH + margin > window.innerHeight) top = window.innerHeight - cardH - margin
    if (top < margin) top = margin
    card.style.left = `${left}px`
    card.style.top = `${top}px`
  }

  // animate in
  requestAnimationFrame(() => {
    overlay.style.opacity = '1'
    card.style.opacity = '1'
    if (isMobile) {
      card.style.transform = 'translateY(0)'
    } else {
      card.style.transform = 'scale(1)'
    }
  })
}

export function closePointCard() {
  const root = portal()
  while (root.firstChild) root.removeChild(root.firstChild)
  currentCardPoint = null
}

let lightboxKeyHandler: ((e: KeyboardEvent) => void) | null = null
// 当前 lightbox 显示的 R2 key + 关联 point + 套娃栈
let currentLightboxKey: string | null = null
let currentLightboxPointId: string | null = null
let lightboxStack: Point[] = [] // 祖先 — pop 时回到上一层
let lightboxBusy = false
let lightboxRipple: Ripple | null = null

function r2KeyFromUrl(url: string): string | null {
  const m = url.match(/^\/api\/img\/(.+)$/)
  return m ? decodeURIComponent(m[1]) : null
}

function pointById(id: string): Point | undefined {
  return currentPointsList.find((x) => x.id === id)
}

/**
 * 入口：从卡片缩略图点 / 从其它 lightbox 推进时调用。
 * 如果 lightbox DOM 已存在，则把当前层入栈，原地 swap 内容；否则全新构建。
 */
export function showLightbox(p: Point) {
  if (!p.dramaUrl) return
  const exists = !!document.getElementById('flip-lightbox')
  if (!exists) {
    lightboxStack = []
    buildLightboxDOM(p)
  } else {
    if (currentLightboxPointId) {
      const cur = pointById(currentLightboxPointId)
      if (cur) lightboxStack.push(cur)
    }
    swapLightboxContent(p)
  }
}

/** 内部：构建一次 DOM 骨架，调一次 applyContent 填初始 */
function buildLightboxDOM(p: Point) {
  closeLightbox()

  const overlay = document.createElement('div')
  overlay.id = 'flip-lightbox'
  overlay.className =
    'fixed inset-0 z-[60] bg-ink-900/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 opacity-0 transition-opacity duration-200'
  overlay.innerHTML = `
    <div data-lb-stage class="relative inline-block max-w-full" style="max-height: calc(100dvh - 32px);">
      <img data-lb-img crossorigin="anonymous" class="result-image block max-w-full object-contain rounded-lg shadow-pop transform scale-95 transition-all duration-200 cursor-crosshair" style="max-height: calc(100dvh - 32px);" />
      <canvas data-lb-ripple class="absolute inset-0 w-full h-full pointer-events-none opacity-0 transition-opacity duration-100 rounded-lg overflow-hidden"></canvas>
      <div data-lb-markers class="absolute inset-0 pointer-events-none"></div>
      <div data-lb-pulse class="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-accent pointer-events-none hidden" style="box-shadow: 0 0 0 0 rgba(216, 85, 58, 0.55); animation: dot-pulse 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;"></div>
      <!-- 底部 caption：scene_zh + meta -->
      <div data-lb-caption class="absolute inset-x-0 bottom-0 px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-t from-black/80 via-black/45 to-transparent text-white pointer-events-none rounded-b-lg"></div>
    </div>
    <div data-lb-status class="absolute bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 text-paper/85 text-[12px] flex items-center transition-opacity opacity-0 z-10">
      <span class="spark"></span><span data-lb-status-text></span>
    </div>
    <div class="absolute top-4 right-4 flex items-center gap-2 z-10">
      <button data-lb-regen class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-paper flex items-center justify-center transition-colors" aria-label="再变一次" title="再变一次">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2 L13.6 8.4 L20 10 L13.6 11.6 L12 18 L10.4 11.6 L4 10 L10.4 8.4 Z"/>
          <path d="M19 16 L19.6 18.4 L22 19 L19.6 19.6 L19 22 L18.4 19.6 L16 19 L18.4 18.4 Z" opacity="0.7"/>
        </svg>
      </button>
      <button data-lb-close class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-paper flex items-center justify-center transition-colors" aria-label="关闭" title="关闭">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </div>
  `

  const stage = overlay.querySelector('[data-lb-stage]') as HTMLElement
  const lbImg = overlay.querySelector('[data-lb-img]') as HTMLImageElement
  const lbRippleCanvas = overlay.querySelector('[data-lb-ripple]') as HTMLCanvasElement
  const pulse = overlay.querySelector('[data-lb-pulse]') as HTMLElement
  const statusBox = overlay.querySelector('[data-lb-status]') as HTMLElement
  const statusText = overlay.querySelector('[data-lb-status-text]') as HTMLElement

  lightboxRipple = new Ripple(lbRippleCanvas, lbImg)

  // overlay 空白点击 → pop 一级（栈空才整体关）
  overlay.addEventListener('click', (e) => {
    const t = e.target as HTMLElement
    if (
      t.closest('[data-lb-regen]') ||
      t.closest('[data-lb-close]') ||
      t.closest('[data-lb-img]') ||
      t.closest('[data-lb-stage]')
    ) {
      // close 自己有处理；img/stage 内的事件由内部 handler 处理
      if (t.closest('[data-lb-close]')) {
        e.stopPropagation()
        closeLightbox()
      }
      return
    }
    popLightbox()
  })
  overlay.querySelector('[data-lb-close]')?.addEventListener('click', (e) => {
    e.stopPropagation()
    closeLightbox()
  })

  overlay.querySelector('[data-lb-regen]')?.addEventListener('click', (e) => {
    e.stopPropagation()
    const cur = pointById(currentLightboxPointId || '')
    if (!cur) return
    closeLightbox()
    closePointCard()
    onRegenAtPoint?.(cur)
  })

  // 点击图片局部 → 把当前显示的 drama 当 source 再生成；完成后**先弹卡片**，不直接换图
  lbImg.addEventListener('click', async (e) => {
    e.stopPropagation()
    if (lightboxBusy || !currentLightboxKey || !currentUser) return
    const r = lbImg.getBoundingClientRect()
    const xRatio = (e.clientX - r.left) / r.width
    const yRatio = (e.clientY - r.top) / r.height
    if (xRatio < 0 || xRatio > 1 || yRatio < 0 || yRatio > 1) return

    const stageRect = stage.getBoundingClientRect()
    pulse.style.left = `${e.clientX - stageRect.left}px`
    pulse.style.top = `${e.clientY - stageRect.top}px`
    pulse.classList.remove('hidden')

    lightboxBusy = true
    lbImg.style.filter = 'brightness(0.55)'
    startLightboxStatus(statusText, statusBox)
    const stopRipple = lightboxRipple?.start(xRatio, yRatio, true)

    try {
      const res = await lightboxGenerate(
        currentLightboxKey,
        xRatio,
        yRatio,
        currentUser,
        currentLightboxPointId || undefined,
      )
      if (res.point) {
        currentPointsList.push(res.point)
        onPointsUpdate?.(res.point)
        // 同款 stage 体感：生成完先弹卡片（带新点位的 thumb + scene），
        // 用户点 thumb 才推进到 lightbox 下一层
        stopRipple?.()
        lbImg.style.filter = ''
        // 把当前 lightbox 的 markers 刷新（新点位是当前层的 child，应该出现一个白点+头像）
        renderLightboxMarkers(stage, lbImg)
        showPointCard(res.point, lbImg)
      }
    } catch (err: any) {
      stopRipple?.()
      lbImg.style.filter = ''
      statusText.textContent = `没出来：${err.message || err}`.slice(0, 60)
    } finally {
      stopLightboxStatus(statusBox)
      pulse.classList.add('hidden')
      lightboxBusy = false
    }
  })

  document.body.appendChild(overlay)
  requestAnimationFrame(() => {
    overlay.style.opacity = '1'
    lbImg.style.transform = 'scale(1)'
  })

  lightboxKeyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') popLightbox()
  }
  window.addEventListener('keydown', lightboxKeyHandler)

  // 应用初始内容
  swapLightboxContent(p)
}

/** 切换/初始填充 lightbox 内容（图、caption、子点位 markers） */
function swapLightboxContent(p: Point) {
  if (!p.dramaUrl) return
  const overlay = document.getElementById('flip-lightbox')
  if (!overlay) return
  const lbImg = overlay.querySelector('[data-lb-img]') as HTMLImageElement
  const stage = overlay.querySelector('[data-lb-stage]') as HTMLElement
  const captionEl = overlay.querySelector('[data-lb-caption]') as HTMLElement
  const markersLayer = overlay.querySelector('[data-lb-markers]') as HTMLElement

  currentLightboxPointId = p.id
  currentLightboxKey = r2KeyFromUrl(p.dramaUrl)

  const writeCaption = () => {
    captionEl.innerHTML = renderLightboxCaption(p)
  }
  const renderMarkers = () => {
    markersLayer.innerHTML = ''
    renderLightboxMarkers(stage, lbImg)
  }

  // 第一次（DOM 刚建立）：直接 set src + render
  if (!lbImg.src || lbImg.src === window.location.href) {
    lbImg.src = p.dramaUrl
    writeCaption()
    const onLoaded = () => renderMarkers()
    if (lbImg.complete && lbImg.naturalWidth > 0) onLoaded()
    else lbImg.addEventListener('load', onLoaded, { once: true })
    return
  }

  // 切换：crossfade
  markersLayer.innerHTML = ''
  lbImg.classList.add('result-image-hidden')
  setTimeout(() => {
    lbImg.src = p.dramaUrl!
    writeCaption()
    const onLoaded = () => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          lbImg.classList.remove('result-image-hidden')
          renderMarkers()
        }),
      )
    }
    if (lbImg.complete && lbImg.naturalWidth > 0) onLoaded()
    else lbImg.addEventListener('load', onLoaded, { once: true })
  }, 200)
}

function renderLightboxCaption(p: Point): string {
  const dt = p.direction === 'before' ? '前情' : '后续'
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  return `
    <div class="max-w-[640px] mx-auto pointer-events-none">
      <div class="flex items-center gap-2 mb-1.5 text-[11px] text-white/85">
        <img src="${avatarUrl(p.userAvatarSeed)}" class="w-4 h-4 rounded-full ring-1 ring-white/40" />
        <span class="text-white font-medium">${escape(p.userName)}</span>
        <span class="text-white/50">·</span>
        <span>点了「${escape(p.targetLabel)}」</span>
        <span class="ml-auto text-[10px] bg-white/20 backdrop-blur-sm rounded-full px-1.5 py-0.5">${dt} · ${escape(p.timeOffset)}</span>
      </div>
      <div class="text-[13px] sm:text-[14px] leading-relaxed line-clamp-3">${escape(p.sceneZh)}</div>
    </div>`
}

/** 把当前层的子点位（chain points）以"白锚 + 小头像"形式渲染到 lightbox 图上，行为对齐 stage 的 dots */
function renderLightboxMarkers(stage: HTMLElement, _lbImg: HTMLImageElement) {
  // 复用 .point-anchor / .point-avatar 样式 — 与 stage dots 完全一致
  // 但要把 stage 当作 dots-layer 一样使用：先清旧
  stage.querySelectorAll('[data-lb-anchor], [data-lb-avatar]').forEach((el) => el.remove())
  if (!currentLightboxPointId) return
  const children = currentPointsList.filter(
    (p) => p.parentPointId === currentLightboxPointId && p.status === 'done',
  )
  for (const cp of children) {
    const anchor = document.createElement('button')
    anchor.dataset.lbAnchor = cp.id
    anchor.className = 'point-anchor'
    anchor.style.left = `${cp.xRatio * 100}%`
    anchor.style.top = `${cp.yRatio * 100}%`
    anchor.setAttribute('aria-label', `${cp.userName} 在 ${cp.targetLabel}`)
    anchor.addEventListener('mouseenter', () => showPointCard(cp, _lbImg))
    anchor.addEventListener('click', (e) => {
      e.stopPropagation()
      // 进入下一层 lightbox（push）
      showLightbox(cp)
    })

    const avatar = document.createElement('button')
    avatar.dataset.lbAvatar = cp.id
    avatar.className = 'point-avatar'
    avatar.style.left = `calc(${cp.xRatio * 100}% + 10px)`
    avatar.style.top = `calc(${cp.yRatio * 100}% - 22px)`
    avatar.title = `${cp.userName} · ${cp.targetLabel}`
    const ai = document.createElement('img')
    ai.src = avatarUrl(cp.userAvatarSeed, 48)
    ai.alt = cp.userName
    ai.className = 'w-full h-full object-cover'
    avatar.appendChild(ai)
    avatar.addEventListener('mouseenter', () => showPointCard(cp, _lbImg))
    avatar.addEventListener('click', (e) => {
      e.stopPropagation()
      // 触屏 fallback：tap 头像也开卡片
      showPointCard(cp, _lbImg)
    })

    stage.appendChild(anchor)
    stage.appendChild(avatar)
  }
}

/** 从 lightbox 退一层：栈非空就回上一层，栈空就整体关 */
function popLightbox() {
  if (lightboxStack.length > 0) {
    const prev = lightboxStack.pop()!
    swapLightboxContent(prev)
  } else {
    closeLightbox()
  }
}

const LB_STATUS_LINES = [
  '让画面起雾…',
  '等显影…',
  '画面在低语…',
  '让边缘软下来…',
  '让影子说话…',
  '再等 5–10 秒…',
  '奇迹时刻还差几秒…',
]
let lbStatusTimer: number | null = null

function startLightboxStatus(textEl: HTMLElement, boxEl: HTMLElement) {
  let i = Math.floor(Math.random() * LB_STATUS_LINES.length)
  textEl.textContent = LB_STATUS_LINES[i]
  boxEl.style.opacity = '1'
  if (lbStatusTimer) clearInterval(lbStatusTimer)
  lbStatusTimer = window.setInterval(() => {
    i = (i + 1) % LB_STATUS_LINES.length
    textEl.textContent = LB_STATUS_LINES[i]
  }, 2200)
}

function stopLightboxStatus(boxEl: HTMLElement) {
  if (lbStatusTimer) {
    clearInterval(lbStatusTimer)
    lbStatusTimer = null
  }
  setTimeout(() => {
    if (!lightboxBusy) boxEl.style.opacity = '0'
  }, 1200)
}

export function closeLightbox() {
  if (lbStatusTimer) {
    clearInterval(lbStatusTimer)
    lbStatusTimer = null
  }
  lightboxBusy = false
  currentLightboxKey = null
  currentLightboxPointId = null
  lightboxRipple = null
  lightboxStack = []
  const el = document.getElementById('flip-lightbox')
  if (el) el.remove()
  if (lightboxKeyHandler) {
    window.removeEventListener('keydown', lightboxKeyHandler)
    lightboxKeyHandler = null
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
